import { hasStatBaseline, type ForumCountFields } from './forumStatChange';
import { resolveForumDescription } from './updateForumDescriptions';

type Counts = {
  topics_count: number;
  posts_count: number;
  members_count: number;
};

type PrevMonth = Pick<
  ForumCountFields,
  | 'previous_month_topics_count'
  | 'previous_month_posts_count'
  | 'previous_month_members_count'
>;

export type BaselinePatch = Partial<PrevMonth>;

/**
 * Eksik "önceki ay" bazını doldurur: sayı > 0 ama baz yoksa mevcut sayıyı baz yazar.
 * Mevcut anlamlı bazlara dokunmaz (üzerine yazmaz).
 */
export function buildMissingBaselinePatch(counts: Counts, prev: PrevMonth): BaselinePatch {
  const patch: BaselinePatch = {};
  if (counts.topics_count > 0 && !hasStatBaseline(prev.previous_month_topics_count)) {
    patch.previous_month_topics_count = counts.topics_count;
  }
  if (counts.posts_count > 0 && !hasStatBaseline(prev.previous_month_posts_count)) {
    patch.previous_month_posts_count = counts.posts_count;
  }
  if (counts.members_count > 0 && !hasStatBaseline(prev.previous_month_members_count)) {
    patch.previous_month_members_count = counts.members_count;
  }
  return patch;
}

type DescriptionForum = {
  forum_id: string;
  name: string;
  category?: string | null;
  members_count?: number | null;
  posts_count?: number | null;
  topics_count?: number | null;
};

/**
 * SEO açıklamasını güncel sayılara göre yeniden üretir.
 * Değişiklik yoksa veya üretilen metin boşsa boş patch döner.
 */
export function buildDescriptionPatch(
  forum: DescriptionForum,
  currentDescription: string | null | undefined
): { description: string } | Record<string, never> {
  const description = resolveForumDescription(forum);
  if (!description || description === (currentDescription ?? '')) return {};
  return { description };
}

/**
 * Bir forum güncellemesinde otomatik bakım yaması:
 * eksik önceki-ay bazı + (gerekiyorsa) yeniden üretilen açıklama.
 * (Geçmiş kaydı/snapshot ayrı olarak recordForumStatSnapshot ile eklenir.)
 */
export function buildForumAutoMaintenancePatch(params: {
  counts: Counts;
  prevMonth: PrevMonth;
  forum: DescriptionForum;
  currentDescription: string | null | undefined;
  regenerateDescription: boolean;
}): BaselinePatch & { description?: string } {
  const baseline = buildMissingBaselinePatch(params.counts, params.prevMonth);
  const desc = params.regenerateDescription
    ? buildDescriptionPatch(params.forum, params.currentDescription)
    : {};
  return { ...baseline, ...desc };
}
